import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarDays,
  Clock3,
  EyeOff,
  FileSearch,
  Filter,
  Lock,
  MessageSquare,
  RefreshCw,
  Search,
  ShieldCheck,
  User,
  X,
} from "lucide-react";
import { supabase, getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import {
  ADMIN_DECRYPT_REASONS,
  buildAdminConversationSessions,
  buildAdminDecryptReason,
  filterAdminConversationSessions,
} from "../lib/adminConversationAudit.mjs";
import type {
  AdminConversationAccessLog,
  AdminConversationAuditSession,
} from "../lib/adminConversationAudit.mjs";

type ConversationRow = {
  id: string;
  utilisateur: string;
  role: "user" | "assistant" | "human";
  contenu_chiffre: string;
  created_at: string;
  profile_name?: string | null;
  twilio_numero?: string | null;
  praticien_id?: string | null;
};

type ClientRow = {
  twilio_numero: string;
  raison_sociale?: string | null;
};

type PractitionerRow = {
  id: string;
  prenom?: string | null;
  nom?: string | null;
  email?: string | null;
};

type DecryptedMessage = {
  id: string;
  role: "user" | "assistant" | "human";
  content: string;
  created_at: string;
  sender_name?: string | null;
  delivery_status?: string | null;
  patient_reaction?: string | null;
};

type AccessState = "all" | "accessed" | "never_accessed";

const ADMIN_PAGE_SIZE = 1000;
const IN_FILTER_BATCH_SIZE = 100;
const CONVERSATION_SELECT =
  "id, utilisateur, role, contenu_chiffre, created_at, profile_name, twilio_numero, praticien_id";

function uniqueStrings(values: Array<string | null | undefined>) {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

async function fetchAllConversationRows(): Promise<ConversationRow[]> {
  const rows: ConversationRow[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const to = from + ADMIN_PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from("conversations")
      .select(CONVERSATION_SELECT)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) throw error;

    const page = (data ?? []) as ConversationRow[];
    rows.push(...page);
    hasMore = page.length === ADMIN_PAGE_SIZE;
    from += ADMIN_PAGE_SIZE;
  }

  return rows;
}

async function fetchClientRows(twilios: string[]): Promise<ClientRow[]> {
  const clients: ClientRow[] = [];

  for (let index = 0; index < twilios.length; index += IN_FILTER_BATCH_SIZE) {
    const chunk = twilios.slice(index, index + IN_FILTER_BATCH_SIZE);
    const { data, error } = await supabase
      .from("clients")
      .select("twilio_numero, raison_sociale")
      .in("twilio_numero", chunk);

    if (error) throw error;

    clients.push(...((data ?? []) as ClientRow[]));
  }

  return clients;
}

async function fetchPractitionerRows(practitionerIds: string[]): Promise<PractitionerRow[]> {
  const practitioners: PractitionerRow[] = [];

  for (let index = 0; index < practitionerIds.length; index += IN_FILTER_BATCH_SIZE) {
    const chunk = practitionerIds.slice(index, index + IN_FILTER_BATCH_SIZE);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, prenom, nom, email")
      .in("id", chunk);

    if (error) throw error;

    practitioners.push(...((data ?? []) as PractitionerRow[]));
  }

  return practitioners;
}

async function fetchAllAccessLogs(): Promise<AdminConversationAccessLog[]> {
  const logs: AdminConversationAccessLog[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const to = from + ADMIN_PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from("decryption_logs")
      .select("conversation_id, decrypted_at")
      .order("decrypted_at", { ascending: false })
      .range(from, to);

    if (error) throw error;

    const page = (data ?? []) as AdminConversationAccessLog[];
    logs.push(...page);
    hasMore = page.length === ADMIN_PAGE_SIZE;
    from += ADMIN_PAGE_SIZE;
  }

  return logs;
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTimeRange(session: AdminConversationAuditSession) {
  return `${formatDateTime(session.sessionStart)} - ${formatDateTime(session.sessionEnd)}`;
}

function roleLabel(role: string) {
  if (role === "user") return "Patient";
  if (role === "assistant") return "Assistant";
  if (role === "human") return "Team";
  return role;
}

function messageAlign(role: string) {
  return role === "user" ? "justify-start" : "justify-end";
}

function messageBubble(role: string) {
  if (role === "user") return "bg-slate-100 text-slate-900 rounded-bl-sm";
  if (role === "assistant") return "bg-emerald-600 text-white rounded-br-sm";
  return "bg-blue-600 text-white rounded-br-sm";
}

export default function ConversationTableAdmin() {
  const user = useUser();
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerRow[]>([]);
  const [accessLogs, setAccessLogs] = useState<AdminConversationAccessLog[]>([]);
  const [selectedSessionKey, setSelectedSessionKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decryptError, setDecryptError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reasonKind, setReasonKind] = useState("Customer support");
  const [reasonDetail, setReasonDetail] = useState("");
  const [decryptedBySession, setDecryptedBySession] = useState<
    Record<string, DecryptedMessage[]>
  >({});

  const [searchTerm, setSearchTerm] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [practitionerFilter, setPractitionerFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [accessState, setAccessState] = useState<AccessState>("all");

  const fetchData = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    setError(null);

    try {
      const nextRows = await fetchAllConversationRows();
      setRows(nextRows);

      const twilios = uniqueStrings(nextRows.map((row) => row.twilio_numero));
      const practitionerIds = uniqueStrings(nextRows.map((row) => row.praticien_id));

      const [nextClients, nextPractitioners, nextAccessLogs] = await Promise.all([
        fetchClientRows(twilios),
        fetchPractitionerRows(practitionerIds),
        fetchAllAccessLogs(),
      ]);

      setClients(nextClients);
      setPractitioners(nextPractitioners);
      setAccessLogs(nextAccessLogs);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to load audit data";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const normalizedPractitioners = useMemo(
    () =>
      practitioners.map((practitioner) => ({
        id: practitioner.id,
        full_name:
          [practitioner.prenom, practitioner.nom].filter(Boolean).join(" ").trim() ||
          practitioner.email ||
          practitioner.id,
      })),
    [practitioners],
  );

  const sessions = useMemo(
    () =>
      buildAdminConversationSessions(
        rows,
        clients,
        normalizedPractitioners,
        accessLogs,
      ),
    [rows, clients, normalizedPractitioners, accessLogs],
  );

  const filteredSessions = useMemo(
    () =>
      filterAdminConversationSessions(sessions, {
        search: searchTerm,
        clinicName: clinicFilter,
        practitionerId: practitionerFilter,
        date: dateFilter,
        accessState,
      }),
    [sessions, searchTerm, clinicFilter, practitionerFilter, dateFilter, accessState],
  );

  useEffect(() => {
    if (filteredSessions.length === 0) {
      setSelectedSessionKey(null);
      return;
    }
    if (!selectedSessionKey || !filteredSessions.some((s) => s.sessionKey === selectedSessionKey)) {
      setSelectedSessionKey(filteredSessions[0].sessionKey);
    }
  }, [filteredSessions, selectedSessionKey]);

  const selectedSession =
    filteredSessions.find((session) => session.sessionKey === selectedSessionKey) ??
    sessions.find((session) => session.sessionKey === selectedSessionKey) ??
    null;

  const decryptedMessages = selectedSession
    ? decryptedBySession[selectedSession.sessionKey] ?? []
    : [];

  const clinicOptions = useMemo(
    () => [...new Set(sessions.map((session) => session.clinicName))].sort(),
    [sessions],
  );

  const practitionerOptions = useMemo(
    () =>
      [...new Map(sessions.map((session) => [session.praticienId, session])).values()]
        .filter((session) => session.praticienId)
        .map((session) => ({
          id: session.praticienId,
          label: session.practitionerName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [sessions],
  );

  if (!user || user.role !== "admin") {
    return (
      <div className="text-center text-gray-500 py-10">
        Access reserved for administrators.
      </div>
    );
  }

  async function requestSessionAccess() {
    if (!selectedSession || !selectedSession.praticienId) return;

    const detail = reasonDetail.trim();
    if (!detail) {
      setDecryptError("Add a specific audit reason before accessing this session.");
      return;
    }

    setDecrypting(true);
    setDecryptError(null);

    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/conversations/decrypt-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify({
            praticien_id: selectedSession.praticienId,
            utilisateur: selectedSession.utilisateur,
            message_ids: selectedSession.messageIds,
            reason: buildAdminDecryptReason(reasonKind, detail),
          }),
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setDecryptedBySession((prev) => ({
        ...prev,
        [selectedSession.sessionKey]: data.messages ?? [],
      }));
      setAccessLogs((prev) => [
        ...prev,
        {
          conversation_id: selectedSession.firstMessageId,
          decrypted_at: new Date().toISOString(),
        },
      ]);
      setModalOpen(false);
      setReasonDetail("");
    } catch (err) {
      setDecryptError(err instanceof Error ? err.message : "Unable to decrypt session");
    } finally {
      setDecrypting(false);
    }
  }

  function clearFilters() {
    setSearchTerm("");
    setClinicFilter("");
    setPractitionerFilter("");
    setDateFilter("");
    setAccessState("all");
  }

  function hideDecryptedContent(sessionKey: string) {
    setDecryptedBySession((prev) => {
      const next = { ...prev };
      delete next[sessionKey];
      return next;
    });
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-6rem)] flex-col gap-4 text-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-950">Admin conversation audit</h1>
          <p className="text-sm text-slate-500">
            {sessions.length} conversations grouped from {rows.length} messages
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="grid gap-3 lg:grid-cols-[minmax(220px,1.5fr)_repeat(4,minmax(150px,1fr))_auto]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search clinic, practitioner, patient or phone"
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-slate-400 focus:bg-white"
            />
          </label>

          <label className="relative block">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select
              value={clinicFilter}
              onChange={(event) => setClinicFilter(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-slate-400 focus:bg-white"
            >
              <option value="">All clinics</option>
              {clinicOptions.map((clinic) => (
                <option key={clinic} value={clinic}>
                  {clinic}
                </option>
              ))}
            </select>
          </label>

          <label className="relative block">
            <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select
              value={practitionerFilter}
              onChange={(event) => setPractitionerFilter(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-slate-400 focus:bg-white"
            >
              <option value="">All practitioners</option>
              {practitionerOptions.map((practitioner) => (
                <option key={practitioner.id} value={practitioner.id ?? ""}>
                  {practitioner.label}
                </option>
              ))}
            </select>
          </label>

          <label className="relative block">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(event) => setDateFilter(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-slate-400 focus:bg-white"
            />
          </label>

          <label className="relative block">
            <ShieldCheck className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <select
              value={accessState}
              onChange={(event) => setAccessState(event.target.value as AccessState)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-slate-400 focus:bg-white"
            >
              <option value="all">All access states</option>
              <option value="accessed">Already accessed</option>
              <option value="never_accessed">Never accessed</option>
            </select>
          </label>

          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            <Filter className="h-4 w-4" />
            Clear
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">Conversations</div>
            <div className="text-xs text-slate-500">
              {filteredSessions.length} shown
            </div>
          </div>

          <div className="max-h-[calc(100vh-19rem)] overflow-y-auto">
            {filteredSessions.map((session) => {
              const isSelected = session.sessionKey === selectedSessionKey;
              return (
                <button
                  key={session.sessionKey}
                  type="button"
                  onClick={() => setSelectedSessionKey(session.sessionKey)}
                  className={`w-full border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 ${
                    isSelected ? "bg-slate-50 ring-1 ring-inset ring-slate-300" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-sm font-semibold text-slate-900">
                          {session.patientName !== "-" ? session.patientName : session.phone}
                        </div>
                        {session.hasAccessLog && (
                          <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
                            Accessed
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500">
                        {session.clinicName} / {session.practitionerName}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="h-3.5 w-3.5" />
                          {formatTimeRange(session)}
                        </span>
                        <span>{session.messageCount} messages</span>
                        <span>{session.lastEventLabel}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {!loading && filteredSessions.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-14 text-center text-slate-400">
                <FileSearch className="h-8 w-8" />
                <div className="text-sm font-medium">No matching session</div>
              </div>
            )}
          </div>
        </div>

        <aside className="min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
          {!selectedSession ? (
            <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3 px-6 text-center text-slate-400">
              <Lock className="h-8 w-8" />
              <div className="text-sm font-medium">Select a session</div>
            </div>
          ) : (
            <div className="flex h-full min-h-[360px] flex-col">
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-950">
                      {selectedSession.patientName !== "-"
                        ? selectedSession.patientName
                        : selectedSession.phone}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {selectedSession.phone}
                    </div>
                  </div>
                  {decryptedMessages.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => hideDecryptedContent(selectedSession.sessionKey)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      <EyeOff className="h-3.5 w-3.5" />
                      Hide
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setDecryptError(null);
                        setModalOpen(true);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-slate-700"
                    >
                      <Lock className="h-3.5 w-3.5" />
                      Request access
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-b border-slate-100 px-4 py-3 text-xs">
                <div>
                  <div className="font-medium text-slate-400">Clinic</div>
                  <div className="mt-1 text-slate-800">{selectedSession.clinicName}</div>
                </div>
                <div>
                  <div className="font-medium text-slate-400">Practitioner</div>
                  <div className="mt-1 text-slate-800">{selectedSession.practitionerName}</div>
                </div>
                <div>
                  <div className="font-medium text-slate-400">Window</div>
                  <div className="mt-1 text-slate-800">{formatTimeRange(selectedSession)}</div>
                </div>
                <div>
                  <div className="font-medium text-slate-400">Roles</div>
                  <div className="mt-1 text-slate-800">
                    {selectedSession.roles.map(roleLabel).join(", ")}
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/60 px-4 py-4">
                {decryptedMessages.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
                    <Lock className="mx-auto h-8 w-8 text-slate-300" />
                    <div className="mt-3 text-sm font-medium text-slate-700">
                      Content hidden until audited access is granted
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {selectedSession.messageCount} messages / {selectedSession.lastEventLabel}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                      Access logged on the server for this session.
                    </div>
                    {decryptedMessages.map((message) => (
                      <div key={message.id} className={`flex ${messageAlign(message.role)}`}>
                        <div
                          className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm shadow-sm ${messageBubble(
                            message.role,
                          )}`}
                        >
                          <div className="mb-1 flex items-center gap-2 text-[11px] opacity-75">
                            <span>{roleLabel(message.role)}</span>
                            {message.sender_name && <span>{message.sender_name}</span>}
                            <span>{formatDateTime(message.created_at)}</span>
                            {message.delivery_status && <span>{message.delivery_status}</span>}
                          </div>
                          <div className="whitespace-pre-line break-words leading-snug">
                            {message.content}
                          </div>
                          {message.patient_reaction && (
                            <div className="mt-1 text-xs opacity-80">
                              Reaction: {message.patient_reaction}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>

      {modalOpen && selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-slate-950">Request audited access</h2>
                <p className="text-sm text-slate-500">
                  {selectedSession.clinicName} / {selectedSession.phone}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Reason category</span>
                <select
                  value={reasonKind}
                  onChange={(event) => setReasonKind(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-slate-400"
                >
                  {ADMIN_DECRYPT_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">Specific context</span>
                <textarea
                  value={reasonDetail}
                  onChange={(event) => setReasonDetail(event.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder="Example: investigating delivery issue reported by Ailah Clinic"
                />
              </label>

              {decryptError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {decryptError}
                </div>
              )}

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Access is recorded server-side before decrypted content is returned.
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={requestSessionAccess}
                disabled={decrypting || !reasonDetail.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" />
                {decrypting ? "Requesting..." : "Log and decrypt"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
